import { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";

interface OrganizationSelectorProps {
  variant?: "topbar" | "sidebar";
}

export const OrganizationSelector = ({
  variant = "topbar",
}: OrganizationSelectorProps) => {
  const { currentOrganization, userOrganizations } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOrganizationSwitch = (organizationId: string) => {
    // Navigate to the new organization URL instead of using the switchOrganization function
    navigate(`/org/${organizationId}`);
    setIsOpen(false);
  };

  if (!currentOrganization) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center ${
          variant === "topbar"
            ? "px-3 py-2 text-sm font-medium rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            : "w-full p-3 rounded-lg bg-gray-800 hover:bg-gray-700 transition-all"
        }`}
      >
        {currentOrganization.logo_url ? (
          <img
            src={currentOrganization.logo_url}
            alt={currentOrganization.name}
            className="w-6 h-6 mr-2 rounded-full"
          />
        ) : (
          <div
            className={`rounded-full flex items-center justify-center ${
              variant === "topbar"
                ? "w-6 h-6 mr-2 bg-blue-500 text-white"
                : "w-8 h-8 mr-2 bg-primary text-white"
            }`}
          >
            {currentOrganization.name.charAt(0).toUpperCase()}
          </div>
        )}
        <span className="truncate max-w-[120px]">
          {currentOrganization.name}
        </span>
        <svg
          className="w-4 h-4 ml-1"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          ></path>
        </svg>
      </button>

      {isOpen && (
        <div
          className={`absolute z-10 mt-2 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 ${
            variant === "topbar"
              ? "w-56 right-0 bg-white dark:bg-gray-800"
              : "w-full left-0 bg-gray-800"
          }`}
        >
          <div className="py-1" role="menu" aria-orientation="vertical">
            <div
              className={`px-4 py-2 text-xs ${
                variant === "topbar"
                  ? "text-gray-500 dark:text-gray-400"
                  : "text-gray-400 border-b border-gray-700"
              }`}
            >
              Your organizations
            </div>
            <div className="max-h-60 overflow-y-auto">
              {userOrganizations.map((org) => (
                <button
                  key={org.organization_id}
                  onClick={() => handleOrganizationSwitch(org.organization_id)}
                  className={`block w-full text-left px-4 py-2 text-sm ${
                    currentOrganization.id === org.organization_id
                      ? variant === "topbar"
                        ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                        : "bg-gray-700 text-white"
                      : variant === "topbar"
                      ? "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      : "text-gray-300 hover:bg-gray-700"
                  }`}
                  role="menuitem"
                >
                  <div className="flex items-center">
                    {org.organizations.logo_url ? (
                      <img
                        src={org.organizations.logo_url}
                        alt={org.organizations.name}
                        className="w-5 h-5 mr-2 rounded-full"
                      />
                    ) : (
                      <div
                        className={`w-5 h-5 mr-2 rounded-full flex items-center justify-center text-xs ${
                          variant === "topbar"
                            ? "bg-blue-500 text-white"
                            : "bg-primary text-white"
                        }`}
                      >
                        {org.organizations.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="truncate">{org.organizations.name}</span>
                    {org.role === "owner" && (
                      <span
                        className={`ml-auto text-xs px-1.5 py-0.5 rounded-full ${
                          variant === "topbar"
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                            : "bg-blue-900 text-blue-200"
                        }`}
                      >
                        Owner
                      </span>
                    )}
                    {org.role === "admin" && (
                      <span
                        className={`ml-auto text-xs px-1.5 py-0.5 rounded-full ${
                          variant === "topbar"
                            ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                            : "bg-purple-900 text-purple-200"
                        }`}
                      >
                        Admin
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
            <div
              className={`pt-1 ${
                variant === "topbar"
                  ? "border-t border-gray-200 dark:border-gray-700"
                  : "border-t border-gray-700"
              }`}
            >
              <Link
                to="/organizations/create"
                preventScrollReset={true}
                className={`block px-4 py-2 text-sm ${
                  variant === "topbar"
                    ? "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    : "text-gray-300 hover:bg-gray-700"
                }`}
                role="menuitem"
                onClick={() => setIsOpen(false)}
              >
                <div className="flex items-center">
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                  Create new organization
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
